// Typed wrapper around GET /api/damper_curve: the bench dyno F-V curve a
// damper (code + clicks) choice resolves to, for the edit-sheet preview.
// Wire shape is validated through the Zod schema below.
//
// Curves arrive in SI (velocity m/s, force N) exactly as the bench stores
// them; the chart converts to in/s + lbf for display (see DamperEditSheet).
import { z } from "zod";

export const CurveSchema = z.object({
  velocity_mps: z.array(z.number()),
  force_N: z.array(z.number()),
});
export type DamperCurve = z.infer<typeof CurveSchema>;

// confidence is the short match label ("exact" / "nearest" / "code_only"),
// not a number; the caption reads it directly. matched_clicks is the resolved
// dyno row's [LSC, HSC, LSR, HSR], null when the row carries no clicks.
export const CurveMatchSchema = z.object({
  confidence: z.string(),
  matched_clicks: z.array(z.number().int()).length(4).nullable(),
  curve_id: z.string().nullable(),
});
export type DamperCurveMatch = z.infer<typeof CurveMatchSchema>;

export const DamperCurveResponseSchema = z.object({
  available: z.boolean(),
  reason: z.string().optional(),
  curve: CurveSchema.optional(),
  match: CurveMatchSchema.optional(),
  baseline: CurveSchema.optional(),
});
export type DamperCurveResponse = z.infer<typeof DamperCurveResponseSchema>;

export type Corner = "lf" | "rf" | "lr" | "rr";

export type DamperCurveQuery = {
  testId: number;
  corner: Corner;
  damperCode: string;
  clicks: [number, number, number, number];
};

export async function fetchDamperCurve(
  q: DamperCurveQuery,
  opts: { signal?: AbortSignal } = {},
): Promise<DamperCurveResponse> {
  const params = new URLSearchParams({
    test_id: String(q.testId),
    corner: q.corner,
    damper_code: q.damperCode,
    lsc: String(q.clicks[0]),
    hsc: String(q.clicks[1]),
    lsr: String(q.clicks[2]),
    hsr: String(q.clicks[3]),
  });
  const r = await fetch(`/api/damper_curve?${params.toString()}`, {
    signal: opts.signal,
  });
  if (!r.ok) {
    throw new Error(`GET /api/damper_curve failed: ${r.status}`);
  }
  return DamperCurveResponseSchema.parse(await r.json());
}
